export function rectanglesOverlap(a, b) {
  return a.width > 0 && a.height > 0 && b.width > 0 && b.height > 0
    && a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}

export function shouldShowPurchaseBar({ mobile, buttonVisible, buttonBottom, overlay, keyboard }) {
  return mobile && buttonVisible && buttonBottom <= 0 && !overlay && !keyboard;
}

export function stickyPriceText(price, currency) {
  const text = price.trim();
  return currency && !text.includes(currency) ? `${text} ${currency}` : text;
}

export function fallbackChatRect(width, height, bottomOffset = 0) {
  return { left: width - 64, right: width, top: height - 96 - bottomOffset, bottom: height - bottomOffset, width: 64, height: 96 };
}

function setText(element, text) {
  if (element.textContent !== text) element.textContent = text;
}

const BrowserElement = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

class MobilePurchaseBar extends BrowserElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section');
    if (!this.section || this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.addButton = this.querySelector('[data-mm-sticky-add]');
    this.optionButton = this.querySelector('[data-mm-sticky-options]');
    this.addButton.addEventListener('click', () => {
      const button = this.nativeButton();
      if (!button || button.disabled || this.pending) return;
      // Delegate to the existing form so quantity, custom fields and cart behaviour stay native.
      button.click();
    }, { signal });
    this.optionButton.addEventListener('click', () => {
      const target = this.section.querySelector('mm-eyewear-options, variant-picker, .product-form-buttons');
      if (!target) return;
      const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ block: 'center', behavior: reducedMotion ? 'auto' : 'smooth' });
      target.querySelector('button:not([disabled]), input:not([disabled]), select')?.focus({ preventScroll: true });
    }, { signal });
    this.section.addEventListener('variant:selected', () => {
      this.pending = true;
      this.queueUpdate();
    }, { signal });
    this.section.addEventListener('variant:update', () => {
      this.pending = false;
      this.queueUpdate();
    }, { signal });
    this.detailsObserver = new MutationObserver(() => this.queueUpdate());
    const details = this.section.querySelector('.product-details');
    if (details) this.detailsObserver.observe(details, {
      childList: true, subtree: true, characterData: true, attributes: true,
      attributeFilter: ['disabled', 'value', 'checked', 'aria-pressed'],
    });
    this.resizeObserver = new ResizeObserver(() => this.queueUpdate());
    this.resizeObserver.observe(this.section);
    this.queueUpdate();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.detailsObserver?.disconnect();
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.frame);
    document.body.classList.remove('mm-mobile-bar-visible');
  }

  nativeButton() {
    return this.section.querySelector('.product-details product-form-component button[name="add"]');
  }

  queueUpdate() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      refreshMobileControls();
    });
  }

  update(mobile, overlay, keyboard) {
    const button = this.nativeButton();
    const rect = button?.getBoundingClientRect();
    this.hidden = !shouldShowPurchaseBar({
      mobile, buttonVisible: Boolean(rect?.width && rect?.height),
      buttonBottom: rect?.bottom ?? Infinity, overlay, keyboard,
    });
    if (!button) return;
    const price = this.section.querySelector('.product-details product-price .price');
    if (price) setText(this.querySelector('[data-mm-sticky-price]'), stickyPriceText(price.textContent, this.dataset.currency));
    const picker = this.section.querySelector('mm-eyewear-options');
    const optionText = picker
      ? ['frame', 'lens', 'size'].map(key => picker.querySelector(`[data-selected-${key}]`)?.textContent.trim()).filter(Boolean).join(' / ')
      : [...this.section.querySelectorAll('variant-picker input:checked, variant-picker select')].map(e => e.value).join(' / ');
    const option = this.querySelector('[data-mm-sticky-option]');
    setText(option, optionText || this.section.querySelector('h1')?.textContent.trim() || '');
    option.title = option.textContent;
    this.addButton.disabled = button.disabled || Boolean(this.pending);
    setText(this.addButton, this.pending ? 'Updating...' : button.querySelector('.add-to-cart-text__content')?.textContent.trim() || button.textContent.trim() || this.dataset.addLabel);
    if (!this.hidden) document.body.style.setProperty('--mm-mobile-bar-height', `${this.getBoundingClientRect().height}px`);
  }
}

export function refreshMobileControls() {
  const mobile = matchMedia('(max-width: 749px)').matches;
  const overlay = Boolean(document.querySelector('dialog[open], .menu-drawer-container[open], shopify-chat[open]'));
  const keyboard = Boolean(window.visualViewport && window.innerHeight - window.visualViewport.height > 150);
  const bars = [...document.querySelectorAll('mm-mobile-purchase-bar')];
  bars.forEach(bar => bar.update?.(mobile, overlay, keyboard));
  document.body.classList.toggle('mm-mobile-bar-visible', bars.some(bar => !bar.hidden));
  document.body.classList.toggle('mm-mobile-overlay-open', overlay || keyboard);
  const launcher = document.querySelector('shopify-chat')?.shadowRoot?.querySelector('[part="activator"]');
  // Closed chat shadow roots still reserve a small bottom-right touch area.
  const barOffset = bars.some(bar => !bar.hidden) ? Number.parseFloat(document.body.style.getPropertyValue('--mm-mobile-bar-height')) || 76 : 0;
  const launcherRect = launcher?.getBoundingClientRect() || fallbackChatRect(window.innerWidth, window.innerHeight, barOffset);
  const controls = document.querySelectorAll('mm-eyewear-options, variant-picker, mm-eyewear-delivery, .product-form-buttons, .mm-eyewear-card__options, [data-mm-mobile-card] [ref="productTitleLink"]');
  const overlap = mobile && launcherRect && [...controls].some(control => rectanglesOverlap(launcherRect, control.getBoundingClientRect()));
  document.body.classList.toggle('mm-chat-controls-overlap', Boolean(overlap));
}

if (typeof customElements !== 'undefined') {
  if (!customElements.get('mm-mobile-purchase-bar')) customElements.define('mm-mobile-purchase-bar', MobilePurchaseBar);
  let frame;
  const queue = () => {
    if (frame) return;
    frame = requestAnimationFrame(() => { frame = null; refreshMobileControls(); });
  };
  addEventListener('scroll', queue, { passive: true });
  addEventListener('resize', queue, { passive: true });
  addEventListener('pageshow', queue);
  window.visualViewport?.addEventListener('resize', queue);
  document.addEventListener('toggle', queue, true);
  document.addEventListener('click', queue, true);
  document.addEventListener('focusin', queue);
  document.addEventListener('focusout', queue);
  // Modal and chat open states live outside the product section.
  const overlayObserver = new MutationObserver(records => {
    if (records.some(record => record.type === 'childList' || record.attributeName === 'open')) queue();
  });
  overlayObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['open'] });
  queue();
}
